import { Flex } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import type {
  PatchedPublicAccessCredentialUpdate,
  PublicAccessCredentialRead
} from 'src/api/main'
import {
  ContentSpinner,
  QueryErrorAlert,
  RevokeCredentialModal,
  RotateCredentialModal
} from 'src/components'
import { useWorkspaceAppsList } from 'src/features/apps/hooks/useWorkspaceAppsList'
import { paths } from 'src/router/paths'

import {
  CredentialDetailToolbar,
  isCredentialLocked,
  isCredentialRevoked,
  QuickStartCard,
  TokenRevealBanner,
  useCredentialTokenReveal
} from '../../components/credentials'
import { WorkspacePageShell, useWorkspaceRoute } from '../WorkspacePageShell'
import {
  useWorkspacePublicAccessCredential,
  useWorkspacePublicAccessCredentialActions
} from '../WorkspacePublicAccessCredentialsPage/hooks'
import { PublicAccessCredentialDrawer } from '../WorkspacePublicAccessCredentialsPage/PublicAccessCredentialDrawer'

import { OverviewCard } from './OverviewCard'

type Action = { kind: 'edit' } | { kind: 'rotate' } | { kind: 'revoke' } | null

export const WorkspacePublicAccessCredentialDetailPage = () => {
  const { t } = useTranslation()
  const { id, workspaceId, idValid } = useWorkspaceRoute()
  const { credentialId = '' } = useParams<{ credentialId: string }>()
  const navigate = useNavigate()

  const {
    data: credential,
    isLoading,
    isError,
    refetch
  } = useWorkspacePublicAccessCredential(workspaceId, credentialId)

  const actions = useWorkspacePublicAccessCredentialActions(workspaceId)
  // Must match the list page. The edit drawer resolves a saved binding back to an
  // app in this list and drops the ones it cannot find, and saving replaces the
  // whole set — so without system apps here, opening a credential bound to Core
  // and pressing save would silently revoke that binding.
  const { apps, isLoading: appsLoading } = useWorkspaceAppsList(
    idValid ? workspaceId : 0,
    { includeSystem: true }
  )

  const [action, setAction] = useState<Action>(null)
  const closeAction = () => setAction(null)

  const { revealedToken, setRevealedToken, clearRevealed } =
    useCredentialTokenReveal()

  if (!idValid || !credentialId) {
    return <Navigate to={paths.workspaces.publicAccess(id)} replace />
  }

  if (isLoading) {
    return (
      <WorkspacePageShell>
        <ContentSpinner minHeight={240} />
      </WorkspacePageShell>
    )
  }

  if (isError || !credential) {
    return (
      <WorkspacePageShell>
        <Flex vertical gap={16} style={{ padding: '24px 0' }}>
          <QueryErrorAlert onRetry={() => void refetch()} />
        </Flex>
      </WorkspacePageShell>
    )
  }

  const locked = isCredentialLocked(credential.status)
  const revoked = isCredentialRevoked(credential.status)

  const handleUpdate = async (
    _id: string,
    body: PatchedPublicAccessCredentialUpdate
  ) => {
    const result = await actions
      .updateCredential({
        path: { id: credential.id, organization_id: workspaceId },
        body
      })
      .catch(() => null)
    if (result) closeAction()
  }

  const handleRotate = async (expiresAt: string | null, revokeOld: boolean) => {
    const result = await actions
      .rotateCredential({
        path: { id: credential.id, organization_id: workspaceId },
        body: { expires_at: expiresAt, revoke_old_credential: revokeOld }
      })
      .catch(() => null)
    if (!result) return
    closeAction()
    // Rotate returns the new credential + its one-time token; the generated
    // type mistypes the response, so read the real runtime shape.
    const rotated = (result as { new_credential?: { token?: string | null } })
      .new_credential
    if (rotated?.token) setRevealedToken(rotated.token)
  }

  const handleRevoke = async (reason: string | null) => {
    const result = await actions
      .revokeCredential({
        path: { id: credential.id, organization_id: workspaceId },
        body: { reason }
      })
      .catch(() => null)
    if (result) navigate(paths.workspaces.publicAccess(id))
  }

  return (
    <WorkspacePageShell
      breadcrumbExtras={[
        {
          title: t('shell.menu.publicAccess'),
          href: paths.workspaces.publicAccess(id)
        }
      ]}
      breadcrumbLeaf={credential.name}
    >
      <Flex vertical gap={16}>
        <CredentialDetailToolbar
          backTo={paths.workspaces.publicAccess(id)}
          backLabel={t('workspace.publicAccess.detail.back')}
          locked={locked}
          revoked={revoked}
          onEdit={() => setAction({ kind: 'edit' })}
          onRotate={() => setAction({ kind: 'rotate' })}
          onRevoke={() => setAction({ kind: 'revoke' })}
          rotateLabel={t('workspace.publicAccess.rotate')}
          revokeLabel={t('workspace.publicAccess.revoke')}
        />

        {revealedToken ? (
          <TokenRevealBanner token={revealedToken} onDismiss={clearRevealed} />
        ) : null}

        <OverviewCard credential={credential} />
        <QuickStartCard token={revealedToken} />
      </Flex>

      <PublicAccessCredentialDrawer
        open={action?.kind === 'edit'}
        mode="edit"
        credential={credential as PublicAccessCredentialRead}
        apps={apps}
        appsLoading={appsLoading}
        submitting={actions.isUpdating}
        onUpdate={handleUpdate}
        onClose={closeAction}
      />

      <RotateCredentialModal
        open={action?.kind === 'rotate'}
        title={t('workspace.publicAccess.rotateConfirmTitle')}
        description={t('workspace.publicAccess.rotateConfirmBody')}
        okText={t('workspace.publicAccess.rotate')}
        loading={actions.isRotating}
        onConfirm={handleRotate}
        onCancel={closeAction}
      />

      <RevokeCredentialModal
        open={action?.kind === 'revoke'}
        title={t('workspace.publicAccess.revokeConfirmTitle')}
        description={t('workspace.publicAccess.revokeConfirmBody')}
        okText={t('workspace.publicAccess.revoke')}
        loading={actions.isRevoking}
        onConfirm={handleRevoke}
        onCancel={closeAction}
      />
    </WorkspacePageShell>
  )
}
